import { useEffect, useMemo, useRef, useState } from 'react';
import { SonosSpeaker, isSonosPlaying } from '../../services/sonosApi';
import { scheduleSonosActiveRefresh } from '../../services/sonosState';
import { parseSonosTime } from './sonosPlaybackUtils';

export function useLiveTrackProgress(speaker: SonosSpeaker | null | undefined) {
  const playing = isSonosPlaying(speaker?.playback_state);
  const basePosition = useMemo(
    () => parseSonosTime(speaker?.track_position),
    [speaker?.track_position],
  );
  const durationSec = useMemo(
    () => parseSonosTime(speaker?.track_duration),
    [speaker?.track_duration],
  );
  const [livePosition, setLivePosition] = useState<number | null>(basePosition);
  const trackEndTriggeredRef = useRef(false);
  const lastTitleRef = useRef(speaker?.current_title ?? '');

  useEffect(() => {
    setLivePosition(basePosition);
    trackEndTriggeredRef.current = false;
  }, [basePosition, speaker?.current_title, speaker?.id]);

  useEffect(() => {
    const title = speaker?.current_title ?? '';
    if (title && title !== lastTitleRef.current) {
      lastTitleRef.current = title;
      trackEndTriggeredRef.current = false;
    }
  }, [speaker?.current_title]);

  useEffect(() => {
    if (!playing || livePosition == null) return undefined;
    const id = window.setInterval(() => {
      setLivePosition((prev) => (prev == null ? prev : prev + 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [playing, livePosition, durationSec]);

  useEffect(() => {
    if (!playing || livePosition == null || !durationSec || durationSec < 8) return;
    if (livePosition >= durationSec - 1 && !trackEndTriggeredRef.current) {
      trackEndTriggeredRef.current = true;
      scheduleSonosActiveRefresh();
    }
  }, [playing, livePosition, durationSec]);

  const progress = durationSec && livePosition != null && durationSec > 0
    ? livePosition / durationSec
    : 0;

  return { playing, progress, livePosition, durationSec };
}
